import type {
  GaSlideImpressionType,
  UserType,
} from "~/components/popup-container/onboarding/types";

export type Event =
  | AcquisitionEvents
  | AcquisitionNewsletterSignUpEvent
  | AppDownloadEvents
  | ArticleEntityLinkEvent
  | ArticleGalleryWidgetEvents
  | BookmarkEvents
  | CommentButtonEvents
  | LoginEvents
  | MenuEvents
  | MoreOnThisWidgetEvents
  | MyAccountEvents
  | NewsletterSignUpEmailEvent
  | NewsletterSignUpEvent
  | NewsTickerEvents
  | NotificationCenterEvents
  | OnboardingPopUpAppDownloadClickEvents
  | OnboardingPopUpAppDownloadEvents
  | OnboardingPopUpBookmarkArticleEvents
  | OnboardingPopUpCommentingEvents
  | OnboardingPopUpCompleteEvents
  | OnboardingPopUpEvergreenEvents
  | OnboardingPopUpFollowEvents
  | OnboardingPopUpImpressionsEvents
  | OnboardingPopUpNewsletterSignUpEvents
  | OnboardingPopUpRecommendedArticleEvents
  | OnboardingPopUpStartEvents
  | OnboardingPopUpSubscribedContinueEvents
  | OnboardingPopUpSubscribedEvents
  | OnboardingPopUpSubscribedRosettaEvents
  | SeriesWidgetEvents
  | ShareButtonEvents
  | StoryStyleEvents
  | TopPicksBottomEvents
  | TopPicksEvents
  | YouTubeVideoEvents;

export type UntypedEvent = {
  action: string;
  category: string;
  label?: string;
};

// Common
type ArticleIndex = number;
type ArticleUrl = string;
type CurrentLocationUrl = string;
type Destination = string;
type Section = string;

export type NewsTickerEvents = {
  action: `${"Impression" | "Click to Open" | "Close"}/${"breaking" | "others"}`;
  category: "Ticker";
  label: ArticleId;
};

export type NewsletterSignUpEmailEvent = {
  action: `Newsletter Sign Up/${string}/Email`;
  category: string;
  label: CurrentPageUrl;
};

export type NewsletterSignUpEvent = {
  action: `Newsletter Sign Up/${string}`;
  category: string;
  label: CurrentPageUrl;
};

export type AcquisitionNewsletterSignUpEvent = {
  action: `Acquisition/${AcquisitionCampaign}/Register/${string}`;
  category: "Acquisition";
  label: CurrentPageUrl;
};

export type OnboardingPopUpAppDownloadEvents = {
  action: `Onboarding/Pop-up/${UserType}/Download App/Continue to SCMP`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpAppDownloadClickEvents = {
  action: `Onboarding/Pop-up/${UserType}/Download App/Click ${"Google" | "App"} Store`;
  category: "Onboarding/Pop-up";
  label: "";
};

export type OnboardingPopUpBookmarkArticleEvents = {
  action: `Onboarding/Pop-up/${UserType}/${GaSlideImpressionType}/Bookmark`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpCommentingEvents = {
  action: `Onboarding/Pop-up/${UserType}/Commenting`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpCompleteEvents = {
  action: `Onboarding/Pop-up/${UserType}/Complete`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpFollowEvents = {
  action: `Onboarding/Pop-up/${UserType}/Follow`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpEvergreenEvents = {
  action: `Onboarding/Pop-up/${UserType}/${GaSlideImpressionType}`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpImpressionsEvents = {
  action: `Onboarding/Pop-up/${UserType}/${GaSlideImpressionType}/Impressions`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpNewsletterSignUpEvents = {
  action: `Onboarding/Pop-up/${UserType}/Newsletter Signup`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpRecommendedArticleEvents = {
  action: `Onboarding/Pop-up/${UserType}/Recommended Article`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpStartEvents = {
  action: "Onboarding/Pop-up/start";
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpSubscribedEvents = {
  action: `Onboarding/Pop-up/${UserType}/Subscribe Now`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpSubscribedRosettaEvents = {
  action: `Onboarding/Pop-up/Subscribe Now/${string}`;
  category: "Onboarding/Pop-up";
  label: string;
};

export type OnboardingPopUpSubscribedContinueEvents = {
  action: `Onboarding/Pop-up/${UserType}/Subscription/Continue`;
  category: "Onboarding/Pop-up";
  label: "";
};

export type MenuEvents =
  | LineMenuEvents
  | LineMenuAllEvents
  | LineMenuAllOpenEvent
  | MenuOpenEvent
  | MenuCloseEvent
  | MenuClickEvent;

type LineMenuEvents = {
  action: "Linemenu/Click";
  category: "Menu";
  label: `${MenuEditionName}/Linemenu/Click/${SectionName}`;
};

type LineMenuAllOpenEvent = {
  action: "Linemenu/Click";
  category: "Menu";
  label: `${MenuEditionName}/Linemenu/All/Open`;
};

type QueueName = string;
type SectionName = string;
type LineMenuAllEvents = {
  action: "Linemenu/Click";
  category: "Menu";
  label: `${MenuEditionName}/Linemenu/All/${QueueName}/${SectionName}`;
};

type MenuOpenEvent = {
  action: "Menu/Open";
  category: "Menu";
  label: "Menu/Open";
};

type MenuCloseEvent = {
  action: "Menu/Close";
  category: "Menu";
  label: "Menu/Close";
};

type MenuClickEvent = {
  action: "Menu/Click";
  category: "Menu";
  label:
    | `Menu/Highlights/${MenuText}`
    | `Menu/Subscribe`
    | `Menu/Click/${MenuTabName}`
    | `Menu/Click/${MenuTabName}/${MenuText}`
    | `Menu/Click/${MenuTabName}/${MenuParentText}/${MenuText}`
    | `Menu/Click/${MenuSection}/${"Expand" | "Collapse"}`
    | `Menu/Click/Search/${MenuSearchText}`;
};

type MenuEditionName = string;
type MenuTabName = string;
type MenuText = string;
type MenuParentText = string;
type MenuSearchText = string;
type MenuSection = string;

export type NotificationCenterEvents =
  | NotificationCenterCompletionEvents
  | {
      action: `Notification Center/Follow/${"Impression" | "Click"}`;
      category: "Notification Center";
      label: CurrentLocationUrl;
    };

export type NotificationCenterCompletionEvents = {
  action: `Notification Center/${"Follow" | "Unfollow"}/${
    | `${FollowButtonSource}/${FollowType}`
    | `${FollowType}`}`;
  category: "Notification Center";
  label: FollowItemName;
};
export const followButtonSources = ["Article_topic_footer"] as const;
export type FollowButtonSource = (typeof followButtonSources)[number];
type FollowType = "Author" | "Section" | "Topic";
type FollowItemName = string;

export type AcquisitionEvents = AcquisitionFollowEvents;

type AcquisitionFollowEvents = {
  action: `Acquisition/${AcquisitionCampaign}/Follow/${FollowType}`;
  category: "Acquisition";
  label: string;
};

type AcquisitionCampaign = string;

export type LoginEvents = LoginButtonEvents | LoginPopupEvents | LoginSubscriptionMeteringEvents;

type LoginButtonEvents = {
  action: "Login/Click";
  category: "Login";
  label: CurrentPageUrl;
};

type LoginPopupEvents = {
  action: `Login/Pop-up/${"Impression" | "Close"}`;
  category: "Login";
  label: CurrentPageUrl;
};

type LoginSubscriptionMeteringEvents = {
  action: `Subscription Metering/Login Wall/Enlarged/Log In Sign Up Success ${string}`;
  category: "Subscription Metering";
  label: CurrentPageUrl;
};

type CurrentPageUrl = string;
type ArticleId = string;

export type BookmarkEvents = BookmarkHomepageEvents | BookmarkArticleEvents;

type BookmarkHomepageEvents = {
  action: `Bookmark/HomePage/${"Click"}`;
  category: "Bookmark";
};

type BookmarkArticleEvents = {
  action: `Bookmark/Article/${"Click" | "Add" | "Remove"}`;
  category: "Bookmark";
  label: string | CurrentPageUrl;
};

type StoryStyleEvents =
  | StoryStyleImpressionEvents
  | StoryStyleReadMoreEvents
  | StoryStyleNewsletterClickEvents
  | StoryStyleNewsletterImpressionEvents;

type StoryStyleNewsletterClickEvents = {
  action: `Story Card/newsletter/${Section}/Click`;
  category: "Story Card";
  label: Destination;
};

type StoryStyleNewsletterImpressionEvents = {
  action: `Story Card/${Section}/Impression`;
  category: "Story Card";
  label: "newsletter sign up";
  value: 29;
};

type StoryStyleImpressionEvents = {
  action: `Story Card/${Section}/Impression`;
  category: "Story Card";
  value: ArticleIndex;
};

type StoryStyleReadMoreEvents = {
  action: `Story Card/Read more/${Section}/Click`;
  category: "Story Card";
  label: string | ArticleUrl;
  value: ArticleIndex;
};

type MyAccountEvents =
  | MyAccountUpdatePaymentEvents
  | MyAccountAppPromotionMessageEvents
  | MyAccountOverlayEvents;
type MyAccountUpdatePaymentEvents = {
  action: `My Account/Overlay/Update Payment/${"Impression" | "Click"}`;
  category: "My Account";
  label: "";
};
type MyAccountAppPromotionMessageEvents = {
  action: `My Account/Overlay/Download or update/Click`;
  category: "My Account";
  label: "";
};
type MyAccountOverlayEvents = {
  action: `My Account/Overlay/Click`;
  category: "My Account";
  label: string;
};

type CommentButtonEvents = CommentButtonImpressionEvents | CommentButtonClickEvents;
export type CommentButtonImpressionEvents = {
  action: `Comment button/Impression`;
  category: "Comment";
  label: `Comment button/Impression/${CommentButtonLocation}`;
};
export type CommentButtonClickEvents = {
  action: `Comment button/Click`;
  category: "Comment";
  label: `Comment button/Click/${CommentButtonLocation}`;
};
export type CommentButtonLocation = "Article Bottom" | "Article Side" | "Adhesive";

type ShareButtonEvents = {
  action: "Share menu button/Click";
  category: "Share button (Adhesive)";
  label: CurrentLocationUrl;
};

export type MoreOnThisWidgetEvents =
  | {
      action: `More on this Story/Click`;
      category: "More on this Story";
      label: ArticleUrl;
    }
  | {
      action: `More on this Story/Impression`;
      category: "More on this Story";
      label: "";
    };

export type YouTubeVideoEvents = YoutubeVideoControlEvent | YoutubeVideoTimeEvent | YoutubeAdEvents;

type YoutubeVideoControlEvent = {
  action: `${string}/${"Play" | "Pause"}`;
  category: "Youtube video";
  label: string;
};

type YoutubeVideoTimeEvent = {
  action: `${string}/Video progress ${string}`;
  category: "Youtube video";
  label: string;
};

type timeOffset = string;
type YoutubeAdEvents =
  | {
      action: `${string}/ad/${timeOffset}`;
      category: "Youtube video";
      label: string;
    }
  | {
      action: `${string}/ad start`;
      category: "Youtube video";
      label: string;
    };

type ArticleEntityLinkEvent = {
  action: `${"Hard link/Click" | "Inline link/Click"}`;
  category: "Article Inline Link";
  label: string;
};

export type TopPicksEvents = TopPicksImpressionEvent | TopPicksClickEvent;

type TopPicksImpressionEvent = {
  action: `Top Picks/inHouse/${string}/Impression`;
  category: "Top Picks";
  label: string;
};

type TopPicksClickEvent = {
  action: `Top Picks/inHouse/${string}/Click`;
  category: "Top Picks";
  label: string;
};

export type TopPicksBottomEvents = TopPicksBottomImpressionEvent | TopPicksBottomClickEvent;

type TopPicksBottomImpressionEvent = {
  action: `PS_Topic/inHouse/${string}/Impression`;
  category: "Top Picks Bottom";
  label: string;
};

type TopPicksBottomClickEvent = {
  action: `PS_Topic/inHouse/${string}/Click`;
  category: "Top Picks Bottom";
  label: string;
};

export type SeriesWidgetEvents = SeriesWidgetImpressionEvent | SeriesWidgetClickEvent;

// cSpell:ignore Recriculation

type SeriesWidgetImpressionEvent = {
  action: "Series Recirculation Widget/LHS/Impression";
  category: "Series Recriculation Widget";
  label: string;
};

type SeriesWidgetClickEvent = {
  action: "Series Recirculation Widget/LHS/Click";
  category: "Series Recriculation Widget";
  label: string;
};

export type ArticleGalleryWidgetEvents =
  | ArticleGalleryWidgetImpressionAd
  | ArticleGalleryWidgetImpression
  | ArticleGalleryWidgetClick;

type ArticleGalleryWidgetImpressionAd = {
  action: `Article Gallery Widget/photo_gallery/${string}/Impression${"/ads" | ""}`;
  category: "Article Gallery Widget";
  label: `${string} of ${string}`;
};

type ArticleGalleryWidgetImpression = {
  action: "Article Gallery Widget/Impression";
  category: "Article Gallery Widget";
  label: string;
};

type ArticleGalleryWidgetClick = {
  action: "Article Gallery Widget/Click";
  category: "Article Gallery Widget";
  label: string;
};

type AppDownloadEvents = {
  action: `App Download/FB Inapp browser/${"Click" | "Close"}`;
  category: "App Download";
  label: CurrentLocationUrl;
};
